/**
 * KB-internal visibility options hook. KB has three options
 * (organization/workspace/internal) and KB-specific copy, so it can't share
 * with `features/audiences` which has only two options + persona-specific
 * copy. If a 3rd consumer appears, promote a generic primitive to
 * `shared/lib`.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@/entities/user';
import { Building2Icon, LayoutGridIcon, ShieldIcon, UsersIcon } from 'lucide-react';
import { useCurrentWorkspaceName } from '@/shared/hooks';
import { useCurrentOrganizationName } from '@/entities/user';
import type { RadioCardOption } from '@/shared/ui/RadioCardGroup';
import type { KnowledgeBaseFormVisibility } from '@/features/knowledge-bases';

type SpaceVisibilityValue = KnowledgeBaseFormVisibility;

export interface KBVisibilityOption extends RadioCardOption<SpaceVisibilityValue> {
  checkedByDefault: boolean;
}

type KBVisibilityOptionInput = {
  option: Omit<KBVisibilityOption, 'checkedByDefault'>;
  canView: boolean;
};

export interface UseKBVisibilityOptionsArgs {
  /** Offers "specified workspaces". */
  canSelectWorkspaces?: boolean;
  /**
   * Offers "this workspace", a create-time shorthand for granting the caller's own. Editing a
   * shared knowledge base cannot honour it: it already has grants, and "my workspace" says nothing
   * about which, so the edit form names the workspaces outright.
   */
  includeCurrentWorkspace?: boolean;
}

export const useKBVisibilityOptions = ({
  canSelectWorkspaces = false,
  includeCurrentWorkspace = true,
}: UseKBVisibilityOptionsArgs = {}): KBVisibilityOption[] => {
  const { t } = useTranslation('knowledge');

  const organizationName = useCurrentOrganizationName();
  const workspaceName = useCurrentWorkspaceName();
  const {
    canManageOrganizationKnowledgeBases,
    canManageWorkspaceKnowledgeBases,
    canManageFoundationalKnowledgeBases,
  } = usePermissions();

  // Display order. Specified workspaces comes last because its picker opens below the whole
  // group. The first available option is checked by default.
  return useMemo(() => {
    const options: KBVisibilityOptionInput[] = [
      {
        option: {
          value: 'organization',
          label: t('knowledgeBases.useKbVisibilityOptions.organization'),
          description: t('knowledgeBases.useKbVisibilityOptions.organizationAccess', {
            organizationName: organizationName ?? t('knowledgeBases.useKbVisibilityOptions.your'),
          }),
          icon: Building2Icon,
        },
        canView: canManageOrganizationKnowledgeBases,
      },
      {
        option: {
          value: 'workspace',
          label: t('knowledgeBases.useKbVisibilityOptions.workspace'),
          description: t('knowledgeBases.useKbVisibilityOptions.workspaceAccess', {
            workspaceName: workspaceName ?? t('knowledgeBases.useKbVisibilityOptions.your'),
          }),
          icon: UsersIcon,
        },
        canView: canManageWorkspaceKnowledgeBases && includeCurrentWorkspace,
      },
      {
        option: {
          value: 'internal',
          label: t('knowledgeBases.useKbVisibilityOptions.foundationKnowledge'),
          description: t('knowledgeBases.useKbVisibilityOptions.foundationKnowledgeAccess'),
          icon: ShieldIcon,
        },
        canView: canManageFoundationalKnowledgeBases,
      },
      {
        option: {
          value: 'workspaces',
          label: t('knowledgeBases.useKbVisibilityOptions.specifiedWorkspaces'),
          description: t('knowledgeBases.useKbVisibilityOptions.specifiedWorkspacesAccess'),
          icon: LayoutGridIcon,
        },
        canView: canManageWorkspaceKnowledgeBases && canSelectWorkspaces,
      },
    ];

    const filtered = options.filter((item) => item.canView).map((item) => item.option);

    // First item in the filtered list is checked by default
    return filtered.map((option, index) => ({
      ...option,
      checkedByDefault: index === 0,
    }));
  }, [
    canManageFoundationalKnowledgeBases,
    canManageOrganizationKnowledgeBases,
    canManageWorkspaceKnowledgeBases,
    canSelectWorkspaces,
    includeCurrentWorkspace,
    organizationName,
    t,
    workspaceName,
  ]);
};
